import React, { useMemo, useState } from "react";
import { Row, Col } from "react-bootstrap";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";

import StatisticsCards, { PerformanceSegments } from "../examAnalytics/StatisticsCards";
import AttendanceChart from "../examAnalytics/AttendanceChart";
import MarksDistributionChart, { PassFailDonut, TopBottomChart } from "../examAnalytics/MarksChart";
import StudentLeaderboard, { TopPerformerCards, TopPerformersTable, LowestPerformersTable } from "../examAnalytics/StudentLeaderboard";
import ClassComparison from "../examAnalytics/ClassComparison";
import SectionComparison from "../examAnalytics/SectionComparison";
import SubjectComparison from "../examAnalytics/SubjectComparison";
import {
    SubjectMarksBarChart,
    SubjectAccuracyRadar,
    SubjectRightWrongChart,
    RankDistributionChart,
} from "../examAnalytics/SubjectPerformanceChart";
import { mapExamDetailToAnalytics } from "./examReportsHelpers";
import {
    buildScopedReportCard,
    buildReportCardHTML,
    printReportCardHTML,
    findStudentInExam,
} from "./studentReportCard";

function Section({ icon, title, children }) {
    return (
        <div className="lec-panel" style={{ border: "1px solid #f1f5f9", borderRadius: 12, padding: 16, background: "#fff", marginBottom: 16 }}>
            <h6 className="font-weight-bold text-dark mb-3 d-flex align-items-center" style={{ gap: 8 }}>
                <i className={icon} style={{ color: "#2563eb" }} />
                {title}
            </h6>
            {children}
        </div>
    );
}

function SelectablePerformanceSection({ icon, title, childrenTable, childrenGraph, defaultView = "table" }) {
    const [viewMode, setViewMode] = useState(defaultView); // "table" | "graph" | "both"

    return (
        <div className="lec-panel" style={{ border: "1px solid #f1f5f9", borderRadius: 12, padding: 16, background: "#fff", marginBottom: 16 }}>
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap" style={{ gap: 8 }}>
                <h6 className="font-weight-bold text-dark mb-0 d-flex align-items-center" style={{ gap: 8 }}>
                    <i className={icon} style={{ color: "#2563eb" }} />
                    {title}
                </h6>
                {/* View Mode Selector Buttons */}
                <div style={{ background: "#f1f5f9", borderRadius: 8, padding: 3, display: "inline-flex", gap: 3 }}>
                    <button
                        type="button"
                        className="btn btn-sm"
                        style={{
                            fontSize: 11.5,
                            fontWeight: 700,
                            padding: "3px 10px",
                            borderRadius: 6,
                            border: "none",
                            background: viewMode === "table" ? "#fff" : "transparent",
                            color: viewMode === "table" ? "#2563eb" : "#64748b",
                            boxShadow: viewMode === "table" ? "0 1px 4px rgba(0,0,0,0.1)" : "none",
                        }}
                        onClick={() => setViewMode("table")}
                    >
                        <i className="fal fa-table me-1" /> Table
                    </button>
                    <button
                        type="button"
                        className="btn btn-sm"
                        style={{
                            fontSize: 11.5,
                            fontWeight: 700,
                            padding: "3px 10px",
                            borderRadius: 6,
                            border: "none",
                            background: viewMode === "graph" ? "#fff" : "transparent",
                            color: viewMode === "graph" ? "#2563eb" : "#64748b",
                            boxShadow: viewMode === "graph" ? "0 1px 4px rgba(0,0,0,0.1)" : "none",
                        }}
                        onClick={() => setViewMode("graph")}
                    >
                        <i className="fal fa-chart-bar me-1" /> Graph
                    </button>
                    <button
                        type="button"
                        className="btn btn-sm"
                        style={{
                            fontSize: 11.5,
                            fontWeight: 700,
                            padding: "3px 10px",
                            borderRadius: 6,
                            border: "none",
                            background: viewMode === "both" ? "#fff" : "transparent",
                            color: viewMode === "both" ? "#2563eb" : "#64748b",
                            boxShadow: viewMode === "both" ? "0 1px 4px rgba(0,0,0,0.1)" : "none",
                        }}
                        onClick={() => setViewMode("both")}
                    >
                        <i className="fal fa-th-large me-1" /> Both
                    </button>
                </div>
            </div>

            {/* Display based on selection */}
            {(viewMode === "table" || viewMode === "both") && childrenTable}
            {viewMode === "both" && <div className="my-3" style={{ borderTop: "1px solid #f1f5f9" }} />}
            {(viewMode === "graph" || viewMode === "both") && childrenGraph}
        </div>
    );
}

function GroupedComparisonChart({ data, nameKey }) {
    if (!data || data.length === 0) {
        return <div className="text-center text-muted py-3" style={{ fontSize: 13 }}>No data available</div>;
    }
    return (
        <div style={{ width: "100%", height: 220 }}>
            <ResponsiveContainer>
                <BarChart data={data}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey={nameKey} tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip shared={false} />
                    <Legend />
                    <Bar dataKey="averageMarks" name="Avg Marks" fill="#2563eb" radius={[4, 4, 0, 0]} isAnimationActive={false} label={{ position: "top", fontSize: 10, fill: "#2563eb", fontWeight: 700 }} />
                    <Bar dataKey="averageAccuracy" name="Avg Accuracy %" fill="#16a34a" radius={[4, 4, 0, 0]} isAnimationActive={false} label={{ position: "top", fontSize: 10, fill: "#16a34a", fontWeight: 700, formatter: (v) => `${v}%` }} />
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
}

function StudentAnalysisTab({ examDetail, listMeta, authorizedClassNames, authorizedSectionNames, authorizedSubjectNames, orgProfile }) {
    const analytics = useMemo(
        () => mapExamDetailToAnalytics(examDetail, listMeta, { authorizedClassNames, authorizedSectionNames, authorizedSubjectNames }),
        [examDetail, listMeta, authorizedClassNames, authorizedSectionNames, authorizedSubjectNames]
    );

    const [cardError, setCardError] = useState("");

    // Prints one student's Exam Report Card straight from the exam that is already open —
    // no extra fetch. Subject rows, totals and the cohort behind rank / percentile /
    // appeared follow the lecturer's own subjects and sections, never the whole exam.
    const printStudentReportCard = (row) => {
        const student = findStudentInExam(examDetail, {
            student_id: row.student_id || row.id,
            student_name: row.student_name,
            section_name: row.section_name,
        });
        if (!student) {
            setCardError(`No result row found for ${row.student_name} in this exam.`);
            return;
        }
        const card = buildScopedReportCard(examDetail, student, { authorizedSectionNames, authorizedSubjectNames });
        if (!card) {
            setCardError(`${row.student_name}'s result in this exam has none of the subjects or sections assigned to you.`);
            return;
        }
        setCardError("");
        printReportCardHTML(`Report Card — ${student.name}`, buildReportCardHTML(card, examDetail, orgProfile));
    };

    if (analytics.rows.length === 0) {
        return (
            <div className="text-center py-5">
                <i className="fal fa-users-slash text-muted" style={{ fontSize: 32 }} />
                <p className="text-muted mt-2 mb-0">No students found for your accessible class/section in this exam.</p>
            </div>
        );
    }

    return (
        <>
            {cardError && (
                <div
                    className="d-flex align-items-start justify-content-between mb-3"
                    style={{ gap: 10, background: "#fffbeb", border: "1px solid #fde68a", color: "#92400e", borderRadius: 8, padding: "9px 12px", fontSize: 12.5, fontWeight: 600 }}
                >
                    <span>
                        <i className="fal fa-exclamation-triangle me-1" />
                        {cardError}
                    </span>
                    <button type="button" className="btn btn-sm p-0" style={{ color: "#92400e", fontSize: 12 }} onClick={() => setCardError("")}>
                        <i className="fal fa-times" />
                    </button>
                </div>
            )}

            <Section icon="fal fa-chart-pie" title="Overview">
                <div className="pt-3 mb-4" style={{ borderTop: "1px solid #f1f5f9" }}>
                    <StatisticsCards overall={analytics.overall} />
                </div>

                <Row className="g-3 mb-3 mt-4">
                    <Col md={4}>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <div className="text-muted font-weight-bold" style={{ fontSize: 12.5 }}>
                                <i className="fal fa-chart-line me-1 text-primary" /> Attendance
                            </div>
                            <span style={{ background: "#f0fdf4", color: "#15803d", fontWeight: 700, fontSize: 11, padding: "2px 8px", borderRadius: 12, border: "1px solid #bbf7d0" }}>
                                {analytics.overall.attended}/{analytics.overall.totalStudents || 0} Attended
                            </span>
                        </div>
                        <AttendanceChart overall={analytics.overall} />
                    </Col>
                    <Col md={4}>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <div className="text-muted font-weight-bold" style={{ fontSize: 12.5 }}>
                                <i className="fal fa-school me-1 text-success" /> Class Performance
                            </div>
                            <span style={{ background: "#f5f3ff", color: "#7c3aed", fontWeight: 700, fontSize: 11, padding: "2px 8px", borderRadius: 12, border: "1px solid #ddd6fe" }}>
                                {(analytics.classComparison || []).length} Classes
                            </span>
                        </div>
                        <GroupedComparisonChart data={analytics.classComparison} nameKey="name" />
                    </Col>
                    <Col md={4}>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <div className="text-muted font-weight-bold" style={{ fontSize: 12.5 }}>
                                <i className="fal fa-layer-group me-1 text-info" /> Section Performance
                            </div>
                            <span style={{ background: "#ecfeff", color: "#0891b2", fontWeight: 700, fontSize: 11, padding: "2px 8px", borderRadius: 12, border: "1px solid #a5f3fc" }}>
                                {(analytics.sectionComparison || []).length} Sections
                            </span>
                        </div>
                        <GroupedComparisonChart data={analytics.sectionComparison} nameKey="name" />
                    </Col>
                </Row>
            </Section>
            {/* 
            <Section icon="fal fa-layer-group" title="Performance Segments">
                <PerformanceSegments segments={analytics.segments} totalAttended={analytics.overall.attended} />
            </Section> */}

            <Section icon="fal fa-table" title="Student Performance">
                <div className="mt-1">
                    <StudentLeaderboard rows={analytics.leaderboard} onPrintReportCard={printStudentReportCard} />
                </div>
            </Section>

            {/* ── 1 Row 2 Columns: Top 10 & Bottom 10 with View Selectors ── */}
            <Row className="g-3 mb-3">
                <Col md={6}>
                    <SelectablePerformanceSection
                        icon="fal fa-trophy"
                        title="Top 10 Students"
                        defaultView="table"
                        childrenTable={<TopPerformersTable students={analytics.topPerformers} onPrintReportCard={printStudentReportCard} />}
                        childrenGraph={<TopBottomChart data={analytics.topPerformers} color="#16a34a" height={260} />}
                    />
                </Col>

                <Col md={6}>
                    <SelectablePerformanceSection
                        icon="fal fa-hand-holding-heart"
                        title="Students Needing Support (Bottom 10)"
                        defaultView="table"
                        childrenTable={<LowestPerformersTable students={analytics.lowestPerformers} onPrintReportCard={printStudentReportCard} />}
                        childrenGraph={<TopBottomChart data={analytics.lowestPerformers} color="#dc2626" height={260} />}
                    />
                </Col>
            </Row>

            {/* ── Previous Separate Sections (Preserved as Comments) ──
            <Section icon="fal fa-trophy" title="Top 10 Students">
                <TopBottomChart data={analytics.topPerformers} color="#16a34a" height={260} />
            </Section>

            <Section icon="fal fa-hand-holding-heart" title="Students Needing Support (Bottom 10)">
                <LowestPerformersTable students={analytics.lowestPerformers} />
                <div className="mt-3">
                    <TopBottomChart data={analytics.lowestPerformers} color="#dc2626" height={260} />
                </div>
            </Section>
            */}

            {/* <Section icon="fal fa-school" title="Class & Section Performance">
                <h6 className="text-muted mb-2" style={{ fontSize: 12.5, fontWeight: 700 }}>By Class</h6>
                <GroupedComparisonChart data={analytics.classComparison} nameKey="name" />
                <div className="mt-2 mb-4">
                    <ClassComparison data={analytics.classComparison} />
                </div>
                <h6 className="text-muted mb-2" style={{ fontSize: 12.5, fontWeight: 700 }}>By Section</h6>
                <GroupedComparisonChart data={analytics.sectionComparison} nameKey="name" />
                <div className="mt-2">
                    <SectionComparison data={analytics.sectionComparison} />
                </div>
            </Section> */}

            {/* <Section icon="fal fa-book-open" title="Subject Performance">
                <SubjectComparison subjects={analytics.subjects} />
                <Row className="mt-3 g-3">
                    <Col md={6}>
                        <div className="text-muted mb-1" style={{ fontSize: 12.5, fontWeight: 600 }}>Average Marks by Subject</div>
                        <SubjectMarksBarChart subjects={analytics.subjects} />
                    </Col>
                    <Col md={6}>
                        <div className="text-muted mb-1" style={{ fontSize: 12.5, fontWeight: 600 }}>Accuracy by Subject</div>
                        <SubjectAccuracyRadar subjects={analytics.subjects} />
                    </Col>
                    <Col md={6}>
                        <div className="text-muted mb-1" style={{ fontSize: 12.5, fontWeight: 600 }}>Right vs Wrong</div>
                        <SubjectRightWrongChart subjects={analytics.subjects} />
                    </Col>
                    <Col md={6}>
                        <div className="text-muted mb-1" style={{ fontSize: 12.5, fontWeight: 600 }}>Rank Distribution</div>
                        <RankDistributionChart data={analytics.rankDistribution} />
                    </Col>
                </Row>
            </Section> */}
        </>
    );
}

export default StudentAnalysisTab;
